'use client';

import { Counter } from '@/components/ui/counter';
import { Section } from '@/components/layout/Section';

interface Stat {
  value: number;
  label: string;
  suffix?: string;
  prefix?: string;
}

interface StatsBarProps {
  stats?: Stat[];
  /** Visual treatment. Default is cream with zellige overlay. */
  variant?: 'cream' | 'navy';
}

const DEFAULT_STATS: Stat[] = [
  { value: 12500, label: 'Annonces actives', suffix: '+' },
  { value: 380, label: 'Agences partenaires', suffix: '+' },
  { value: 75, label: 'Villes couvertes' },
  { value: 16, label: 'Années d’expertise' },
];

/**
 * Editorial stats bar — Fraunces display numerals over a subtle zellige overlay.
 * Counters animate when scrolled into view (respects prefers-reduced-motion).
 */
export function StatsBar({ stats = DEFAULT_STATS, variant = 'cream' }: StatsBarProps) {
  const tone = variant === 'navy' ? 'navy' : 'cream';
  const numberCls =
    variant === 'navy' ? 'text-accent' : 'text-ink';
  const labelCls =
    variant === 'navy' ? 'text-surface-light/70' : 'text-ink-secondary';
  const dividerCls =
    variant === 'navy' ? 'bg-surface-light/10' : 'bg-ink/10';

  return (
    <Section tone={tone} spacing="md" noContainer>
      <div className="relative">
        <div className="container-page relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-0 md:divide-x divide-ink/10">
            {stats.map((s, i) => (
              <div
                key={i}
                className={
                  i > 0
                    ? `md:pl-8 lg:pl-12 ${variant === 'navy' ? '[&]:divide-surface-light/10' : ''}`
                    : ''
                }
              >
                <div className={`font-display text-display leading-none ${numberCls}`}>
                  <Counter
                    to={s.value}
                    prefix={s.prefix}
                    suffix={s.suffix}
                    duration={1.6}
                    format={(n) => Math.round(n).toLocaleString('fr-FR')}
                  />
                </div>
                <p className={`mt-3 text-sm ${labelCls}`}>{s.label}</p>
              </div>
            ))}
          </div>
          {/* divider override for navy variant */}
          <style jsx>{`
            .grid > div + div {
              ${variant === 'navy' ? 'border-color: rgba(250, 250, 247, 0.10);' : ''}
            }
          `}</style>
          {variant === 'navy' && (
            <span className={`sr-only ${dividerCls}`} aria-hidden />
          )}
        </div>
      </div>
    </Section>
  );
}
